import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import qs from "qs";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import InputNumeric from "../../../components/InputNumeric";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function CompanyUpdatePopup({
    open,
    onClose,
    onSuccess,
    title = "Update Company",
    company, // MUST include company_uuid
    parentCompanyId = null,
}) {
    const { t } = useTranslation();

    const [initLoading, setInitLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [inputCompany, setInputCompany] = useState("");
    const [selectedCompanyGroup, setSelectedCompanyGroup] = useState(0);
    const [selectedBoundary, setSelectedBoundary] = useState(0);

    const [listCompanyGroup, setListCompanyGroup] = useState([]);

    const savingRef = useRef(false);

    const companyId = useMemo(() => {
        return company?.company_uuid || company?.id || "";
    }, [company]);

    const apiPost = useCallback(async (path, data) => {
        const res = await axios({
            withCredentials: true,
            method: "POST",
            headers: { "content-type": "application/x-www-form-urlencoded" },
            data: qs.stringify(data || {}),
            url: `${process.env.REACT_APP_BASE_SERVER}${path}`,
        });
        return res?.data;
    }, []);

    const resetForm = useCallback(() => {
        setInputCompany("");
        setSelectedCompanyGroup(0);
        setSelectedBoundary(0);
        setListCompanyGroup([]);
    }, []);

    useEffect(() => {
        if (!open) {
            savingRef.current = false;
            resetForm();
            return;
        }

        if (!companyId) return;

        let mounted = true;
        setInitLoading(true);

        (async () => {
            try {
                // ✅ fetch both first, then set state (no pending state needed)
                const [divisionRes, readRes] = await Promise.all([
                    apiPost("/client/division", {}),
                    apiPost("/client/company/read", { id: companyId }),
                ]);

                if (!mounted) return;

                const divisions = divisionRes?.success ? divisionRes?.data || [] : [];
                setListCompanyGroup(divisions);

                const row = readRes?.success ? readRes?.data?.[0] : null;
                if (!row) {
                    await Swal.fire({
                        title: "",
                        html: "<p>Failed to load company data</p>",
                        confirmButtonColor: "#3085d6",
                    });
                    return;
                }

                setInputCompany(row.company_name || "");
                setSelectedBoundary(Number(row.company_shareholding || 0));

                const gid = Number(row.company_group || 0);

                // ✅ only set if option exists, otherwise keep 0
                const exists = divisions.some((g) => Number(g.group_id) === gid);
                setSelectedCompanyGroup(exists ? gid : 0);
            } catch (e) {
                console.error(e);
                Swal.fire({
                    title: "",
                    html: "<p>Failed to load company / division data</p>",
                    confirmButtonColor: "#3085d6",
                });
            } finally {
                if (mounted) setInitLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [apiPost, companyId, open, resetForm]);

    const optionCompanyGroup = useMemo(() => {
        const opts = [
            <option key="placeholder" value={0} disabled>
                Select Business Division
            </option>,
        ];

        listCompanyGroup.forEach((g) => {
            opts.push(
                <option key={g.group_id} value={Number(g.group_id)}>
                    {g.group_name}
                </option>
            );
        });

        return opts;
    }, [listCompanyGroup]);

    const handleClose = useCallback(() => {
        if (saving) return;
        onClose?.();
    }, [saving, onClose]);

    const onSaveRecord = useCallback(async () => {
        if (savingRef.current) return;
        if (saving || initLoading) return;

        // eslint-disable-next-line
        if (inputCompany != "" && selectedCompanyGroup != 0 && selectedBoundary != 0) {
            const payload = {
                name: inputCompany,
                id: companyId,
                group: Number(selectedCompanyGroup),
                boundary: Number(selectedBoundary),

                company_name: inputCompany,
                company_group: Number(selectedCompanyGroup),
                company_shareholding: Number(selectedBoundary),
                ...(parentCompanyId ? { parent_company_uuid: parentCompanyId } : {}),
            };

            try {
                savingRef.current = true;
                setSaving(true);

                const resp = await apiPost("/client/company/update", payload);

                if (resp?.success) {
                    setSaving(false);
                    savingRef.current = false;

                    onSuccess?.(resp);

                    Swal.fire({
                        title: "",
                        html: "<p>Successfully record company</p>",
                        confirmButtonColor: "#3085d6",
                    });

                    return;
                }
            } catch (e) {
                console.error(e);
            } finally {
                setSaving(false);
                savingRef.current = false;
            }
        }

        Swal.fire({
            title: "",
            html: "<p>Please fill all information needs</p>",
            confirmButtonColor: "#3085d6",
        });
    }, [
        apiPost,
        companyId,
        initLoading,
        inputCompany,
        onSuccess,
        parentCompanyId,
        saving,
        selectedBoundary,
        selectedCompanyGroup,
    ]);

    return (
        <Dialog
            open={!!open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ pr: 6 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>
                    {(initLoading || saving) && <CircularProgress size={18} sx={{ ml: 1 }} />}
                </Box>

                <IconButton
                    onClick={handleClose}
                    sx={{ position: "absolute", top: 10, right: 10 }}
                    disabled={saving}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="company" className="form-label">
                                {t("menu.label622")}
                            </label>
                            <input
                                id="company"
                                name="company"
                                type="text"
                                className="form-control"
                                value={inputCompany}
                                onChange={(e) => setInputCompany(e.target.value)}
                                placeholder={t("emission.form.enter")}
                                disabled={saving || initLoading}
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="companyGroup" className="form-label">
                                {t("menu.label621")}
                            </label>
                            <select
                                onChange={(e) => setSelectedCompanyGroup(Number(e.target.value))}
                                value={Number(selectedCompanyGroup)}
                                id="companyGroup"
                                name="companyGroup"
                                className="form-select mb-3"
                                disabled={saving || initLoading}
                            >
                                {optionCompanyGroup}
                            </select>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="shareholding" className="form-label">
                                {t("company.label1")}
                            </label>
                            <div className="input-group">
                                <InputNumeric
                                    id="shareholding"
                                    name="shareholding"
                                    isAllowed={(values) => {
                                        if (!values.value) return true;
                                        const { floatValue } = values;
                                        return floatValue < 101;
                                    }}
                                    value={selectedBoundary}
                                    onValueChange={(values, sourceInfo) => {
                                        if (sourceInfo?.event?.target?.name) {
                                            const next = values?.value ?? "";
                                            setSelectedBoundary(next === "" ? 0 : Number(next));
                                        }
                                    }}
                                    placeholder={t("emission.form.enter")}
                                    disabled={saving || initLoading}
                                />
                                <span className="input-group-text">%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} variant="outlined" disabled={saving}>
                    {t("general.btnCancel") || "Cancel"}
                </Button>
                <Button onClick={onSaveRecord} variant="contained" disabled={saving || initLoading}>
                    {t("general.btnSave")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
